import React from 'react';
import { Card as AntCard, Button as AntButton } from 'antd';
import { ReadOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { User } from '@sentry/types';
import { urlDomainName } from 'src/utils/isValidUrl';
import { Post } from 'src/slices/post';
import { Tag } from 'src/slices/tag';

import Image from '../components/Image';
import { CustomTag } from './Tags';

const useStyles = createUseStyles({
  card: {
    borderRadius: '5px',
    boxShadow: '0 0 5px #ccc',
    height: 'auto',
    '& .ant-card-head-title ': {
      whiteSpace: 'pre-wrap',
      fontWeight: 'bolder',
      color: '#5c5c5c',
    },
    '& .ant-card-body': {
      padding: '5% !important',
    },
    marginBottom: '20px',
  },
  cardTitle: {
    minHeight: '48px',
    paddingBottom: '10px',
    fontSize: '16px',
    background: 'transparent',
    borderBottom: '1px solid #f0f0f0',
    borderRadius: '2px 2px 0 0',
    color: '#5c5c5c',
    fontWeight: 'bolder',
    whiteSpace: 'pre-wrap',
  },
  cardDescription: {
    marginTop: '10px',
    color: 'rgba(0, 0, 0, 0.45)',
  },
  cardContent: {
    height: 'auto',
  },
  articleInfoPart: {
    marginBottom: '5%',
  },
  articleTags: {
    marginTop: '5px',
  },
  cardArticleDescription: {
    height: 'auto',
    marginBottom: '5%',
  },
  cardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '5%',
    float: 'right',
  },
  readBox: {
    display: 'inline-block',
  },
});

interface ArticleCardProps {
  className?: string;
  post: Post;
  tags: Tag[];
  users: { [userId: string]: User };
  loadingArticles: boolean;
  setLoadingArticles: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ArticleCard: React.VFC<
  ArticleCardProps & React.HTMLAttributes<HTMLDivElement>
> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const openArticle = (post: Post) => {
    history.push(`/posts/${post._id}`);
  };

  const getTagsFromPost = (postTagId: string) => {
    const postTag = props.tags.find((tag) => tag._id === postTagId);
    return postTag !== undefined ? `${postTag.name}` : '';
  };

  return (
    <AntCard
      className={classes.card}
      loading={props.loadingArticles}
      cover={props.post.image && <Image src={props.post.image} />}
      hoverable={true}
      onClick={() => openArticle(props.post)}
    >
      <div className={classes.cardTitle}>{props.post.title}</div>
      <div className={classes.cardDescription}>
        <div className={classes.cardContent}>
          <div className={classes.articleInfoPart}>
            <div className="articlePublisher">
              {props.post?.publisher || props.post?.url ? (
                <a href={props.post?.url}>
                  {props.post?.publisher
                    ? props.post?.publisher
                    : urlDomainName(props.post?.url)}
                </a>
              ) : null}
            </div>
            <div className="articleDate">
              {props.post.datePublished
                ? `${t('article.published')} ${moment(
                    props.post?.datePublished,
                  ).fromNow()}`
                : null}
            </div>
            <div className="articleAddedUser">
              {`${t('tags.articleAdded')}
                            ${
                              props.post?.authorId
                                ? props.users[props.post.authorId as string]
                                    ?.firstName
                                : null
                            } ${moment(props.post?.createdAt).fromNow()}
                            `}
            </div>
            <div className={classes.articleTags}>
              {props.post &&
                (props.post.tags as string[]).map((postTag) => (
                  <CustomTag
                    key={postTag}
                    _id={postTag}
                    name={getTagsFromPost(postTag)}
                  />
                ))}
            </div>
          </div>

          <div className={classes.cardArticleDescription}>
            {props.post.description}
          </div>
          <div className={classes.cardBottom}>
            <div key="read" className={classes.readBox}>
              <AntButton
                type="primary"
                shape="round"
                onClick={() => openArticle(props.post)}
              >
                {t('listArticle.open')}
                <ReadOutlined />
              </AntButton>
            </div>
          </div>
        </div>
      </div>
    </AntCard>
  );
};
